import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutsideClick, useUploadAttachments } from '@lua-frontend/numu-ui';
import ReactDOM from 'react-dom';
import { createEditor, Editor, Node, Text, Transforms } from 'slate';
import { Editable, Slate, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import {
  toggleMark,
  toggleBlock,
  isAlignElement,
  isLinkActive,
  unwrapLink,
  wrapLink,
  tryOutdentListItem,
  tryIndentListItem,
  tryDedentListItem,
  isInListItem,
  tryExitLinkOnSpaceOrEnter,
  deserializeHtmlToSlate
} from './text-editor.helper';
import { FixedToolbar } from './toolbar/fixed.toolbar';
import { HoveringToolbar } from './toolbar/hovering.toolbar';
import './styles.scss';
import { MdOutlineInsertDriveFile, MdOutlineClose } from 'react-icons/md';

// Keyboard shortcuts (mod = Cmd on Mac, Ctrl on Windows/Linux), per Slate docs
const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code'
};

// URL validation: must start with http:// or https:// and have non-whitespace after
const URL_VALIDATION_REGEX = /^https?:\/\/\S+$/;

const isHotkey = (hotkey, event) => {
  const mod = event.metaKey || event.ctrlKey;
  const key = event.key?.toLowerCase();
  if (hotkey === 'mod+b') return mod && key === 'b';
  if (hotkey === 'mod+i') return mod && key === 'i';
  if (hotkey === 'mod+u') return mod && key === 'u';
  if (hotkey === 'mod+`') return mod && (key === '`' || event.key === '`');
  if (hotkey === 'mod+k') return mod && key === 'k';
  if (hotkey === 'mod+.') return mod && key === '.';
  if (hotkey === 'mod+-') return mod && (key === '-' || key === 'minus');
  return false;
};

// Serialization helpers
const escapeHtml = (unsafe) => {
  if (!unsafe) return '';
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Serialize list children so depth becomes nested ul/ol (semantic HTML, no inline styles).
const serializeListChildrenToHtml = (listItems, listType, serializeNodeToHtml) => {
  const tag = listType === 'numbered-list' ? 'ol' : 'ul';
  let html = '';
  let currentDepth = 0;
  for (const item of listItems) {
    const depth = item.depth != null ? item.depth : 0;
    const inner = item.children ? item.children.map((n) => serializeNodeToHtml(n)).join('') : '';
    while (currentDepth > depth) {
      html += `</${tag}>`;
      currentDepth -= 1;
    }
    while (currentDepth < depth) {
      html += `<${tag}>`;
      currentDepth += 1;
    }
    const alignStyle = item.align ? ` style="text-align: ${item.align}"` : '';
    html += `<li${alignStyle}>${inner}</li>`;
  }
  while (currentDepth > 0) {
    html += `</${tag}>`;
    currentDepth -= 1;
  }
  return html;
};

const serializeNodeToHtml = (node) => {
  if (Text.isText(node)) {
    const raw = node.text != null ? String(node.text) : '';
    let segment = escapeHtml(raw);

    if (node.bold === true) segment = `<strong>${segment}</strong>`;
    if (node.italic === true) segment = `<em>${segment}</em>`;
    if (node.underline === true) segment = `<u>${segment}</u>`;
    if (node.code === true) segment = `<code>${segment}</code>`;
    const styleParts = [];
    if (node.fontSize) styleParts.push(`font-size: ${node.fontSize}`);
    if (node.color) styleParts.push(`color: ${node.color}`);
    if (styleParts.length) segment = `<span style="${styleParts.join('; ')}">${segment}</span>`;

    return segment;
  }

  const children = node.children ? node.children.map((n) => serializeNodeToHtml(n)).join('') : '';
  const alignStyle = node.align ? ` style="text-align: ${node.align}"` : '';

  switch (node.type) {
    case 'bulleted-list':
      return `<ul${alignStyle}>${serializeListChildrenToHtml(node.children, 'bulleted-list', serializeNodeToHtml)}</ul>`;
    case 'numbered-list':
      return `<ol${alignStyle}>${serializeListChildrenToHtml(node.children, 'numbered-list', serializeNodeToHtml)}</ol>`;
    case 'list-item':
      return `<li${alignStyle}>${children}</li>`;
    case 'block-quote':
      return `<blockquote${alignStyle}>${children}</blockquote>`;
    case 'heading-one':
      return `<h1${alignStyle}>${children}</h1>`;
    case 'heading-two':
      return `<h2${alignStyle}>${children}</h2>`;
    case 'link': {
      const url = escapeHtml(node.url || '');
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
    }
    case 'variable': {
      const key = escapeHtml(node.key || '');
      // Use single-brace syntax for variables in the serialized HTML
      return `{${key}}`;
    }
    default:
      return `<p${alignStyle}>${children}</p>`;
  }
};

const DEFAULT_TOOLBAR_ITEMS = [
  'bullets',
  'numbered-list',
  'bold',
  'italic',
  'underline',
  'code',
  'heading-one',
  'heading-two',
  'block-quote',
  'left',
  'center',
  'right',
  'justify',
  'link'
];

// Treat link as inline so it stays on the same line as surrounding text (Slate defaults to block)
const withInlines = (editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) =>
    element.type === 'link' || element.type === 'variable' ? true : isInline(element);

  editor.isVoid = (element) => (element.type === 'variable' ? true : isVoid(element));

  return editor;
};

/** Plain text for onChange payload: top-level blocks joined with newlines. */
const docPlainText = (nodes) => nodes.map((n) => Node.string(n)).join('\n');

function withTextLimit({ limit=null } = {}) {
  return function Plugin(editor) {
    const { insertText, insertData } = editor;

    editor.insertText = (text) => {
      if (limit == null || limit <= 0) {
        insertText(text);
        return;
      }

      const currentLength = Editor.string(editor, []).length;
      const selectedLength = editor.selection ? Editor.string(editor, editor.selection).length : 0;
      const remaining = limit - (currentLength - selectedLength);
      insertText(String(text).slice(0, remaining));
    };

    editor.insertData = (data) => {
      const pastedText = data.getData('text/plain');
      if (pastedText) {
        // Remove curly braces
        const sanitizedText = String(pastedText)
          .replace(/\r\n/g, '\n')
          .replace(/\{([^{}]+)\}/g, '$1');
        const lines = sanitizedText.split('\n');
        lines.forEach((line, index) => {
          if (index > 0) {
            editor.insertBreak();
          }
          if (line) {
            editor.insertText(line);
          }
        });
        return;
      }
      insertData(data);
    };
    return editor;
  };
}

const EMPTY_VALUE = [
  {
    type: 'paragraph',
    children: [{ text: '' }]
  }
];

// Main reusable RichTextEditor
const RichTextEditor = ({
  className,
  placeholder = 'Type here...',
  disabled = false,
  toolbar = null,
  children,
  resetKey,
  onChange,
  onOutsideClick,
  value: inValue,
  attachments: inAttachments = [],
  assignmentAttachments: inAssignmentAttachments = [],
  onFocus,
  readOnly = false,
  resizable = false,
  initialHeight = 80,
  maxHeight,
  maxLength,
  maxTopLevelBlocks=null,
  onShowAssignmentChange
}) => {
  const editor = useMemo(
    () => withTextLimit({ limit: maxLength ?? null })(withInlines(withHistory(withReact(createEditor())))),
    [maxLength]
  );
  const [editorHeight, setEditorHeight] = useState(initialHeight);

  const [value, setValue] = useState(inValue || EMPTY_VALUE);
  const [attachments, setAttachments] = useState(inAttachments || []);
  const [assignmentAttachments] = useState(inAssignmentAttachments || []);
  const [showAssignment, setShowAssignment] = useState(Boolean(toolbar?.showAssignment));

  const setShowAssignmentInternal = useCallback(
    (next) => {
      setShowAssignment(next);
      if (onShowAssignmentChange) onShowAssignmentChange(next);
    },
    [onShowAssignmentChange]
  );
  // assignmentAttachments chip is intentionally internal; parent-driven updates can be done via resetKey.
  // INSERT LINK RELATED STATES
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkValidationError, setLinkValidationError] = useState(null);

  // REFS
  const inputContainerRef = useRef(null);
  const linkInputRef = useRef(null);
  const toolbarRef = useRef(null);
  const linkBackdropRef = useRef(null);
  const linkModalRef = useRef(null);
  const editableWrapperRef = useRef(null);

  const usableToolbar = useMemo(
    () =>
      toolbar?.type
        ? {
            ...toolbar,
            items: toolbar?.items ?? DEFAULT_TOOLBAR_ITEMS,
            showAssignment,
            onToggleAssignment: setShowAssignmentInternal
          }
        : null,
    [toolbar, showAssignment, setShowAssignmentInternal]
  );

  let uploadAttachments = useUploadAttachments({
    field: { name: 'attachments', value: attachments },
    form: {
      setFieldValue: (name, details) => {
        if (name === 'attachments') {
          let newDetails = [];
          if (Array.isArray(details)) {
            newDetails = details;
          } else {
            newDetails = [details];
          }
          setAttachments(newDetails);
          handleChange(value, newDetails);
        }
      }
    },
    outputType: 'details',
    folderPath: 'creatorInvites.attachments',
    prefix: 'attachment-',
    isPrivate: false,
    apiUrl:
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_NODE_API_ENDPOINT) ||
      (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_NODE_API_URL) ||
      undefined,
    enableCrop: false,
    allowMultiple: true,
    maxSize: usableToolbar?.maxAttachmentsSize
      ? usableToolbar.maxAttachmentsSize * 1024 * 1024
      : null
  });

  const handleRemoveAssignment = useCallback(() => {
    // Just hide the assignment chip and deactivate the toolbar button;
    // keep the underlying assignmentAttachments so it can be shown again.
    setShowAssignmentInternal(false);
  }, [setShowAssignmentInternal]);

  useOutsideClick([linkBackdropRef, linkModalRef, inputContainerRef, toolbarRef], onOutsideClick);

  useEffect(() => {
    if (resetKey !== undefined) {
      setValue(EMPTY_VALUE);
      Transforms.deselect(editor);
    }
  }, [resetKey, editor]);

  useEffect(() => {
    if (showLinkModal) {
      setLinkUrl('');
      setLinkValidationError(null);
      setTimeout(() => linkInputRef.current?.focus(), 0);
    }
  }, [showLinkModal]);

  const handleRemoveAttachment = useCallback(
    (attachment) => {
      setAttachments((prev) => {
        const next = prev.filter((a) => a.key !== attachment.key);
        const plainText = value.map((n) => Node.string(n)).join('\n');
        const html = value.map((n) => serializeNodeToHtml(n)).join('');
        onChange?.({
          plainText,
          html,
          slateValue: value,
          attachments: next,
          assignmentAttachments
        });
        return next;
      });
    },
    [value, onChange, assignmentAttachments]
  );

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const el = editableWrapperRef.current;
    const startHeight = el ? el.getBoundingClientRect().height : 80;

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const delta = moveEvent.clientY - startY;
      setEditorHeight(Math.max(28, startHeight + delta));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);
  }, []);

  const handleDOMBeforeInput = useCallback(
    (event) => {
      if (
        maxLength != null &&
        maxLength > 0 &&
        event.inputType?.startsWith('insert')
      ) {
        const currentLength = Editor.string(editor, []).length;
        const selectedLength = editor.selection ? Editor.string(editor, editor.selection).length : 0;
        if (currentLength - selectedLength >= maxLength) {
          event.preventDefault();
          return;
        }
      }

      switch (event.inputType) {
        case 'formatBold':
          event.preventDefault();
          return toggleMark(editor, 'bold');
        case 'formatItalic':
          event.preventDefault();
          return toggleMark(editor, 'italic');
        case 'formatUnderline':
          event.preventDefault();
          return toggleMark(editor, 'underline');
        case 'formatCode':
          event.preventDefault();
          return toggleMark(editor, 'code');
        default:
          break;
      }
    },
    [editor, maxLength]
  );

  const handleEditableClick = useCallback((event) => {
    const anchor = event.target.closest('a[href]');
    if (anchor) {
      event.preventDefault();
      const href = anchor.getAttribute('href');
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
      return true;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      // Limit paragraph/block count (Slate: top-level editor children).
      // Matches the recommended Slate approach of preventing the operation at input time.
      if (event.key === 'Enter') {
        const max = maxTopLevelBlocks;
        if (max != null && max > 0) {
          const paragraphCount = editor.children.length;
          if (paragraphCount >= max) {
            event.preventDefault(); // Stop creating a new paragraph
            return;
          }
        }
      }

      // When variables are enabled, prevent manual curly-brace typing
      if (usableToolbar?.items?.includes('variables') && (event.key === '{' || event.key === '}')) {
        event.preventDefault();
        return;
      }

      if (tryExitLinkOnSpaceOrEnter(editor, event)) {
        return;
      }
      if (event.key === 'Backspace' && tryOutdentListItem(editor)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Tab' && isInListItem(editor)) {
        event.preventDefault();
        if (event.shiftKey) {
          tryDedentListItem(editor);
        } else {
          tryIndentListItem(editor);
        }
        return;
      }
      if (isHotkey('mod+k', event)) {
        event.preventDefault();
        if (isLinkActive(editor)) {
          unwrapLink(editor);
        } else {
          setShowLinkModal(true);
        }
      }
      if (isHotkey('mod+.', event) || isHotkey('mod+-', event)) {
        event.preventDefault();
        toggleBlock(editor, 'bulleted-list');
        return;
      }
      for (const hotkey of Object.keys(HOTKEYS)) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
          return;
        }
      }
    },
    [editor, usableToolbar, maxTopLevelBlocks]
  );

  const handleChange = (newValue, newAttachments = null) => {
    setValue(newValue);
    const plainText = docPlainText(newValue);
    const html = newValue.map((n) => serializeNodeToHtml(n)).join('');
    let finalAttachments = newAttachments ?? attachments;
    onChange?.({
      plainText,
      html,
      slateValue: newValue,
      attachments: finalAttachments,
      assignmentAttachments
    });
  };

  const handleLinkConfirm = useCallback(
    (url) => {
      if (url && url.trim()) wrapLink(editor, url.trim());
      setShowLinkModal(false);
    },
    [editor]
  );

  const handleInsertLink = useCallback(() => {
    const val = linkUrl.trim();
    if (!val || !URL_VALIDATION_REGEX.test(val)) {
      setLinkValidationError('Please enter a valid URL');
      return;
    }
    handleLinkConfirm(val);
  }, [linkUrl, handleLinkConfirm]);

  const handleLinkModalClose = useCallback(() => {
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkValidationError(null);
  }, []);

  const renderElement = useCallback((props) => {
    const { attributes, children, element } = props;
    const style = isAlignElement(element) ? { textAlign: element.align } : {};
    if (element.type === 'list-item' && (element.depth == null ? 0 : element.depth) > 0) {
      style.marginLeft = `${(element.depth || 0) * 24}px`;
    }

    switch (element.type) {
      case 'block-quote':
        return (
          <blockquote style={style} {...attributes}>
            {children}
          </blockquote>
        );
      case 'bulleted-list':
        return (
          <ul style={style} {...attributes}>
            {children}
          </ul>
        );
      case 'numbered-list':
        return (
          <ol style={style} {...attributes}>
            {children}
          </ol>
        );
      case 'list-item':
        return (
          <li style={style} {...attributes}>
            {children}
          </li>
        );
      case 'heading-one':
        return (
          <h1 style={style} {...attributes}>
            {children}
          </h1>
        );
      case 'heading-two':
        return (
          <h2 style={style} {...attributes}>
            {children}
          </h2>
        );
      case 'link':
        return (
          <a {...attributes} href={element.url} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      case 'variable':
        return (
          <span {...attributes} className="text-editor__variable-token" data-key={element.key}>
            {'{'}
            {element.label || element.key}
            {'}'}
            {children}
          </span>
        );
      default:
        return (
          <p style={style} {...attributes}>
            {children}
          </p>
        );
    }
  }, []);

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.code) children = <code>{children}</code>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    if (leaf.link) {
      children = (
        <a href={leaf.url} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    const spanStyle = { ...(attributes.style || {}) };
    if (leaf.text === '') spanStyle.paddingLeft = '0.1px';
    if (leaf.fontSize) spanStyle.fontSize = leaf.fontSize;
    if (leaf.color) spanStyle.color = leaf.color;

    return (
      <span {...attributes} style={spanStyle}>
        {children}
      </span>
    );
  }, []);

  const calculateFileSize = (size) => {
    let fileSize = (Number.isFinite(size) ? size : 0) / 1024;
    let fileSizeUnit = 'Kb';
    if (fileSize > 1024) {
      fileSize = fileSize / 1024;
      fileSizeUnit = 'Mb';
    }
    if (fileSize > 1024) {
      fileSize = fileSize / 1024;
      fileSizeUnit = 'Gb';
    }
    if (fileSize > 1024) {
      fileSize = fileSize / 1024;
      fileSizeUnit = 'Tb';
    }

    fileSize = fileSize.toFixed(2);

    return { size: fileSize, unit: fileSizeUnit };
  };

  const renderAssignment = () => {
    if (!showAssignment || assignmentAttachments.length === 0) return null;

    const assignment = assignmentAttachments[0];

    const assignmentSize = Number.isFinite(assignment?.size) ? assignment.size : 0;
    const { size, unit } = calculateFileSize(assignmentSize);

    return (
      <div className="text-editor__attachments-container">
        <div className="text-editor__attachment-item">
          <MdOutlineInsertDriveFile size={20} color="var(--grey-50, #797E86)" />

          <span
            style={{
              color: 'var(black, #000)',
              fontFamily: 'Montserrat SemiBold',
              fontSize: 14
            }}
          >
            {assignment?.name || 'campaign_brief.pdf'}
          </span>
          <span
            style={{
              color: 'var(--grey-50, #797E86)',
              fontDamily: 'Montserrat Regular',
              fontSize: 12
            }}
          >
            {size}
            {unit}
          </span>
          <MdOutlineClose
            size={16}
            color="var(--grey-50, #797E86)"
            style={{ cursor: 'pointer', marginLeft: 'auto' }}
            onClick={handleRemoveAssignment}
          />
        </div>
      </div>
    );
  };

  // Same modal pattern as EnhancedDescriptionTextarea: backdrop + overlay (no SweetAlert)
  const linkModalEl =
    showLinkModal && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <>
            <div
              className="text-editor__link-modal-backdrop"
              ref={linkBackdropRef}
              onClick={handleLinkModalClose}
            />
            <div
              className="text-editor__link-modal"
              ref={linkModalRef}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-editor__link-modal-title">Insert Link</h3>
              <input
                ref={linkInputRef}
                type="url"
                className="text-editor__link-input"
                placeholder="Enter URL"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  if (linkValidationError) setLinkValidationError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleInsertLink();
                  }
                  if (e.key === 'Escape') handleLinkModalClose();
                }}
                aria-invalid={!!linkValidationError}
                aria-describedby={linkValidationError ? 'text-editor__link-error' : undefined}
              />
              {linkValidationError && (
                <div id="text-editor__link-error" className="text-editor__link-error" role="alert">
                  {linkValidationError}
                </div>
              )}
              <div className="text-editor__link-actions">
                <button
                  type="button"
                  className="text-editor__link-btn text-editor__link-btn-secondary"
                  onClick={handleLinkModalClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-editor__link-btn text-editor__link-btn-primary"
                  onClick={handleInsertLink}
                >
                  Insert Link
                </button>
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  const renderToolbar = ({ type, position }) => {
    if (type === 'fixed') {
      const renderRightCorner = Boolean(usableToolbar?.variablesMenuRightCorner);
      if (renderRightCorner) return null;

      if (position === usableToolbar?.position) {
        return (
          <FixedToolbar
            toolbarRef={toolbarRef}
            toolbar={usableToolbar}
            onOpenLinkModal={() => setShowLinkModal(true)}
            uploadAttachments={uploadAttachments}
          />
        );
      }
      return null;
    }

    if (type === 'hovering') {
      return (
        <HoveringToolbar
          toolbarRef={toolbarRef}
          inputContainerRef={inputContainerRef}
          toolbar={usableToolbar}
          onOpenLinkModal={() => setShowLinkModal(true)}
          uploadAttachments={uploadAttachments}
        />
      );
    }
  };

  const renderAttachments = () => {
    if (attachments.length === 0) return null;

    return (
      <div className="text-editor__attachments-container">
        {attachments.map((attachment) => {
          const { size, unit } = calculateFileSize(attachment.size);
          return (
            <div key={attachment.key} className="text-editor__attachment-item">
              <MdOutlineInsertDriveFile size={20} color="var(--grey-50, #797E86)" />

              <span
                style={{
                  color: 'var(black, #000)',
                  fontFamily: 'Montserrat SemiBold',
                  fontSize: 14
                }}
              >
                {attachment.name}
              </span>
              <span
                style={{
                  color: 'var(--grey-50, #797E86)',
                  fontDamily: 'Montserrat Regular',
                  fontSize: 12
                }}
              >
                {size}
                {unit}
              </span>
              <MdOutlineClose
                size={16}
                color="var(--grey-50, #797E86)"
                style={{ cursor: 'pointer', marginLeft: 'auto' }}
                onClick={() => handleRemoveAttachment(attachment)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const isRightCornerToolbar =
    usableToolbar?.type === 'fixed' && Boolean(usableToolbar?.variablesMenuRightCorner);

  return (
    <Slate editor={editor} value={value} onChange={handleChange}>
      {linkModalEl}

      {usableToolbar?.type && renderToolbar({ type: usableToolbar.type, position: 'top' })}
      <div ref={inputContainerRef} className="text-editor__wrapper">
        <div
          className={`selection:text-editor__input-cell text-editor__input-cell--resizable ${className || ''}`}
          style={
            resizable
              ? { height: editorHeight, overflowY: 'auto' }
              : maxHeight != null
                ? {
                    maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
                    overflowY: 'auto'
                  }
                : {}
          }
        >
          <div
            ref={editableWrapperRef}
            className={'text-editor__editable-wrapper'}
            style={{ flex: 1 }}
          >
            <Editable
              className="text-editor__input"
              placeholder={placeholder}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              onClick={handleEditableClick}
              onDOMBeforeInput={handleDOMBeforeInput}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              spellCheck
              disabled={disabled}
              readOnly={disabled || readOnly}
            />
          </div>

          {renderAttachments()}
          {renderAssignment()}
          {resizable && (
            <div
              className="text-editor__resize-handle"
              onMouseDown={handleResizeStart}
              title="Drag to resize"
              role="separator"
              aria-orientation="horizontal"
              style={{ touchAction: 'none' }}
            />
          )}
        </div>
        {isRightCornerToolbar && (
          <div
            style={{
              marginLeft: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FixedToolbar
              toolbarRef={toolbarRef}
              toolbar={usableToolbar}
              onOpenLinkModal={() => setShowLinkModal(true)}
              uploadAttachments={uploadAttachments}
              inlineRight
            />
          </div>
        )}
        {children}
      </div>
      {usableToolbar?.type && renderToolbar({ type: usableToolbar.type, position: 'bottom' })}

      {uploadAttachments?.uploadError?.message && (
        <div className="text-editor__error">{uploadAttachments.uploadError.message}</div>
      )}
    </Slate>
  );
};

export { RichTextEditor, deserializeHtmlToSlate };
